import {FC} from 'react';
import {Box, Button, Typography} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {useIntl} from 'react-intl';
import {ApprovalAction} from '../hooks/useStreamChat';
import {approvalSx} from './ApprovalCard.sx';

interface ApprovalCardProps {
    // The agent's own plain-language confirmation sentence (e.g. "Creating a charging
    // schedule for Pl. 205 every Wednesday from 05:00 to 07:00"). Built by the backend
    // from the agent's narration — no tool names or ids reach the user.
    summary: string;
    // Batch mode: every write the agent intends to perform, already validated and
    // scope-corrected by the backend. Approving runs exactly this list.
    actions?: ApprovalAction[];
    // Heading the agent gave the batch, e.g. "Weekly schedule for 4 stations".
    title?: string | null;
    onApprove: () => void;
    onDeny: () => void;
}

// Shown when the agent wants to run write tools (create/update/delete) that require
// a human decision. The backend run is parked until the user chooses.
//
// Two shapes, one card. A single write shows the agent's confirmation sentence. A
// BATCH — a bulk request like a weekly schedule across several stations, which is
// dozens of writes — shows the full list instead, so the user reviews everything at
// once and decides once. Asking per action would mean dozens of identical cards,
// which nobody reads.
export const ApprovalCard: FC<ApprovalCardProps> = ({summary, actions, title, onApprove, onDeny}) => {
    const intl = useIntl();
    const isBatch = Boolean(actions && actions.length > 0);

    return (
        <Box sx={approvalSx.root}>
            <Box sx={approvalSx.header}>
                <WarningAmberOutlinedIcon sx={approvalSx.headerIcon} />
                <Typography sx={approvalSx.title}>
                    {intl.formatMessage({id: 'aiAgentApprovalTitle'})}
                </Typography>
            </Box>

            <Typography sx={approvalSx.subtitle}>
                {isBatch
                    ? intl.formatMessage(
                        {id: 'aiAgentApprovalSubtitleBatch'},
                        {count: actions!.length},
                    )
                    : intl.formatMessage({id: 'aiAgentApprovalSubtitle'})}
            </Typography>

            {isBatch ? (
                <>
                    {title && <Typography sx={approvalSx.planTitle}>{title}</Typography>}
                    <Box sx={approvalSx.actionList}>
                        {actions!.map((action) => (
                            <Box key={action.index} sx={approvalSx.actionRow}>
                                <Typography component="span" sx={approvalSx.actionIndex}>
                                    {action.index + 1}.
                                </Typography>
                                <Typography component="span" sx={approvalSx.actionText}>
                                    {action.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            ) : (
                summary && <Typography sx={approvalSx.summary}>{summary}</Typography>
            )}

            <Box sx={approvalSx.actions}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<BlockOutlinedIcon />}
                    onClick={onDeny}
                    sx={approvalSx.denyButton}
                >
                    {intl.formatMessage({id: 'aiAgentApprovalDeny'})}
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={onApprove}
                    sx={approvalSx.approveButton}
                >
                    {isBatch
                        ? intl.formatMessage(
                            {id: 'aiAgentApprovalApproveAll'},
                            {count: actions!.length},
                        )
                        : intl.formatMessage({id: 'aiAgentApprovalApprove'})}
                </Button>
            </Box>
        </Box>
    );
};
